import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useAuth } from './AuthContext';
import {
  getProjectCapabilities,
  normalizeTimelineSubLabelMode,
  sanitizeProjectTags,
} from '../constants/projectTypes';
import { STUDIO_MY_STUDIO_SEGMENT } from '../constants/studioProjects';
import { useStudioProjectMigration } from '../hooks/useStudioProjectMigration';

const ProjectContext = createContext(null);

const RESERVED_SEGMENTS = new Set([
  STUDIO_MY_STUDIO_SEGMENT,
  'billing',
  'settings',
  'captures',
]);

/**
 * Resolve active studio project id from URL.
 * - /studio/:uid/:projectId — project room
 * - /studio/:uid/:projectId/map — map workspace
 * - /studio/:uid/:projectId/:captureId — capture viewer (last segment is not `map`)
 */
export function resolveActiveStudioProjectIdFromLocation(pathname, uid) {
  if (!pathname || !uid) return null;
  const p = pathname.split('?')[0];
  const parts = p.split('/').filter(Boolean);
  if (parts[0] !== 'studio' || parts[1] !== uid) return null;
  const seg = parts[2];
  if (!seg || RESERVED_SEGMENTS.has(seg)) return null;
  if (parts.length >= 4) {
    return seg;
  }
  if (parts.length === 3) {
    return seg;
  }
  return null;
}

export function ProjectProvider({ children }) {
  const { user } = useAuth();
  useStudioProjectMigration(user?.uid);
  const location = useLocation();
  const params = useParams();
  const studioIdParam = params.studioID ?? null;
  const authUid = user?.uid ?? null;
  const studioOwnerUid = studioIdParam || authUid || null;

  const activeProjectId = useMemo(() => {
    if (!studioOwnerUid) return null;
    const fromParams =
      params.projectId && !RESERVED_SEGMENTS.has(params.projectId)
        ? params.projectId
        : null;
    const fromPath = resolveActiveStudioProjectIdFromLocation(location.pathname, studioOwnerUid);
    return fromParams || fromPath;
  }, [studioOwnerUid, location.pathname, params.projectId]);

  const [activeStudioProjectDoc, setActiveStudioProjectDoc] = useState(null);

  useEffect(() => {
    const ownerUid = studioIdParam || authUid || null;
    if (!ownerUid || !activeProjectId) {
      setActiveStudioProjectDoc(null);
      return undefined;
    }
    let cancelled = false;
    let unsub = () => {};
    (async () => {
      try {
        if (auth.currentUser?.uid) await auth.currentUser.getIdToken();
      } catch (_) {}
      if (cancelled) return;
      const pref = doc(db, 'users', ownerUid, 'studio', activeProjectId);
      unsub = onSnapshot(
        pref,
        (snap) => {
          if (!snap.exists()) {
            setActiveStudioProjectDoc(null);
            return;
          }
          const d = snap.data();
          const tlm = d.timelineLabelMode;
          setActiveStudioProjectDoc({
            id: activeProjectId,
            projectName: typeof d.projectName === 'string' ? d.projectName : '',
            projectTags: sanitizeProjectTags(d.projectTags),
            timelineLabelMode: tlm === 'date' || tlm === 'custom' ? tlm : 'version',
            timelineSubLabelMode: normalizeTimelineSubLabelMode(d.timelineSubLabelMode),
            timelineAutoAdd: d.timelineAutoAdd !== false,
            measurementUnitPref: d.measurementUnitPref === 'mm' ? 'mm' : 'm',
            splatOverMapOpacity:
              typeof d.splatOverMapOpacity === 'number' && Number.isFinite(d.splatOverMapOpacity)
                ? Math.min(1, Math.max(0, d.splatOverMapOpacity))
                : null,
            mapCenter:
              d.mapCenter && typeof d.mapCenter === 'object' && typeof d.mapCenter.lng === 'number' && typeof d.mapCenter.lat === 'number'
                ? { lng: d.mapCenter.lng, lat: d.mapCenter.lat }
                : null,
            mapZoom: typeof d.mapZoom === 'number' && Number.isFinite(d.mapZoom) ? d.mapZoom : null,
            mapBaseStyle: typeof d.mapBaseStyle === 'string' ? d.mapBaseStyle : null,
            mapOverlay: d.mapOverlay && typeof d.mapOverlay === 'object' ? d.mapOverlay : null,
            memberIds: Array.isArray(d.memberIds) ? d.memberIds : [],
            memberRoles: d.memberRoles && typeof d.memberRoles === 'object' ? d.memberRoles : {},
            memberProfiles: d.memberProfiles && typeof d.memberProfiles === 'object' ? d.memberProfiles : {},
            timelineBeforeImageUrl: d.timelineBeforeImageUrl || null,
            timelineBeforeImageStoragePath: d.timelineBeforeImageStoragePath || null,
            timelineFinalImageUrl: d.timelineFinalImageUrl || null,
            timelineFinalImageStoragePath: d.timelineFinalImageStoragePath || null,
          });
        },
        () => setActiveStudioProjectDoc(null),
      );
    })();
    return () => {
      cancelled = true;
      unsub();
    };
  }, [studioIdParam, authUid, activeProjectId]);

  const capabilities = useMemo(() => getProjectCapabilities(), []);

  const measurementUnitPref = useMemo(() => {
    const fromDoc = activeStudioProjectDoc?.measurementUnitPref;
    if (fromDoc === 'mm' || fromDoc === 'm') return fromDoc;
    return capabilities.defaultMeasurementUnit === 'mm' ? 'mm' : 'm';
  }, [activeStudioProjectDoc?.measurementUnitPref, capabilities.defaultMeasurementUnit]);

  const value = useMemo(
    () => ({
      activeProjectId,
      activeStudioProjectDoc,
      capabilities,
      measurementUnitPref,
    }),
    [activeProjectId, activeStudioProjectDoc, capabilities, measurementUnitPref],
  );

  return (
    <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
  );
}

export function useProject() {
  const ctx = useContext(ProjectContext);
  if (!ctx) {
    throw new Error('useProject must be used within ProjectProvider');
  }
  return ctx;
}

/**
 * Safe for routes outside ProjectProvider (e.g. marketing) — returns null.
 */
export function useProjectOptional() {
  return useContext(ProjectContext);
}
